import type { FC } from "react";
import { useMemo, useState } from "react";

import { SWReactCountryIcons } from "@similarweb/icons";
import { Combobox, CountryDropdownButton } from "@similarweb/ui-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import type { ICountryObject } from "app/services/CountryService";
import CountryService from "app/services/CountryService";
import { allTrackers } from "app/services/track/track";

import { SetupFieldLoader } from "./SetupFieldLoader";

interface ILLMSetupCountrySelectorProps {
    selectedCountry: ICountryObject;
    onCountriesChange: (countries: ICountryObject) => void;
    supportedCountriesByChatbot: Record<string, string[]>;
    isLoading: boolean;
}

export const LLMSetupCountrySelector: FC<ILLMSetupCountrySelectorProps> = ({
    selectedCountry,
    onCountriesChange,
    supportedCountriesByChatbot,
    isLoading,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const availableCountries: ICountryObject[] = useMemo(() => {
        if (!supportedCountriesByChatbot) {
            return [];
        }

        const allCountryCodes = new Set<string>();
        Object.values(supportedCountriesByChatbot).forEach((countries) => {
            countries.forEach((code) => allCountryCodes.add(code));
        });

        const uniqueCountryCodes = Array.from(allCountryCodes);
        const countries = CountryService.getCountriesByCodes(uniqueCountryCodes.map((code) => code.toLowerCase()));

        return countries
            .map((country) => ({
                id: country.id,
                text: country.text,
                code: country.code?.toUpperCase(),
            }))
            .sort((a, b) => a.text.localeCompare(b.text));
    }, [supportedCountriesByChatbot]);

    const handleCountrySelect = (countryId: number) => {
        const country = availableCountries.find((item) => item.id === countryId);

        setIsOpen(false);
        setSearchValue("");
        allTrackers.dispatchTrackEvent("Dropdown", "Select", `Ai Brand Visibility/Locations/${country.text}`);
        onCountriesChange(country);
    };

    const filteredCountries = useMemo(() => {
        if (!searchValue) {
            return availableCountries;
        }

        return availableCountries.filter((country) => country.text.toLowerCase().includes(searchValue.toLowerCase()));
    }, [availableCountries, searchValue]);

    const onToggle = (state: boolean) => {
        if (state) {
            allTrackers.dispatchTrackEvent("Dropdown", "Open", `Ai Brand Visibility/Location`);
        } else {
            setSearchValue("");
        }

        setIsOpen(state);
    };

    return (
        <Box
            position="relative"
            borderRadius="3px"
            width="232px"
            height="40px"
        >
            {isLoading ? (
                <SetupFieldLoader />
            ) : (
                <Combobox
                    isOpen={isOpen}
                    onOpenChange={onToggle}
                    placement="bottom-start"
                    matchTriggerWidth
                    data-automation="llm-setup-country-dropdown"
                >
                    <Combobox.Trigger>
                        <Flex width="100%">
                            <CountryDropdownButton
                                countryId={selectedCountry.id}
                                isOpen={isOpen}
                                dataAutomation="llm-setup-country-combobox-trigger"
                            >
                                {selectedCountry.text}
                            </CountryDropdownButton>
                        </Flex>
                    </Combobox.Trigger>
                    <Combobox.SearchInput
                        value={searchValue}
                        onChange={setSearchValue}
                        placeholder={translate("llm.setup.country.search.placeholder")}
                        data-automation="llm-setup-country-search-input"
                    />
                    <Combobox.Content emptyState={translate("dropdown.no.results")}>
                        {filteredCountries.map((country) => (
                            <Combobox.Option
                                key={country.id}
                                id={country.id}
                                textValue={country.text}
                                onClick={() => handleCountrySelect(country.id)}
                                data-automation={`llm-setup-country-option-${country.id}`}
                                data-automation-country={country.id}
                            >
                                <Flex
                                    alignItems="center"
                                    gap="8px"
                                >
                                    <SWReactCountryIcons
                                        countryCode={country.id}
                                        size="sm"
                                    />
                                    <Combobox.OptionLabel>{country.text}</Combobox.OptionLabel>
                                </Flex>
                            </Combobox.Option>
                        ))}
                    </Combobox.Content>
                </Combobox>
            )}
        </Box>
    );
};
